import { z } from 'zod';

/**
 * Schema for line item update when adding new items from document.
 * All monetary values are decimals in the full currency unit.
 */
export const LineItemUpdateSchema = z.object({
  sequenceNumber: z.number(),
  description: z.string(),
  /**
   * Line nature copied from the extracted document line (`lineItems[].lineItemType`).
   * Nullable and required for the same strict Structured Outputs reason as the fiscal codes below.
   */
  lineItemType: z
    .enum(['SERVICE', 'INTEREST', 'FINE', 'DISCOUNT', 'OTHER'])
    .nullable()
    .describe('Copie lineItems[].lineItemType do documento extraído; null quando ausente.'),
  quantity: z.number().nullable(),
  /** Unit price as decimal currency amount (e.g., 550.0 = R$550.00) */
  unitPrice: z.number().nullable(),
  /** Net/line value as decimal currency amount (e.g., 550.0 = R$550.00) */
  netValue: z.number().nullable(),
  productCode: z.string().nullable(),
  /**
   * Fiscal codes. Nullable and **required**, never `.optional()`.
   *
   * They exist on this schema for one reason: `lineItemsAction: 'REPLACE'` deletes the bill's
   * line items and recreates them from this array. Without the codes here, attaching a document
   * to a bill silently destroyed an NCM the bill already had — and the NCM is what the
   * asset-capitalisation engine matches on.
   *
   * The `.optional()` spelling is what an "absent most of the time" field wants, and it is
   * wrong here. This schema is converted by `z.toJSONSchema` and sent to OpenAI Structured
   * Outputs with `strict: true` (`openai-native-provider.ts:466`), which requires **every**
   * property to appear in `required`. An optional field is omitted from that array and the
   * request is rejected outright — so a field meant to improve NF-e reconciliation would have
   * broken reconciliation for every document type. Nullable keeps the field required in the
   * schema and lets the model answer `null`, which is the same thing to every reader here.
   */
  ncmCode: z.string().nullable(),
  cfopCode: z.string().nullable(),
});

/**
 * Schema for tax breakdown update.
 */
export const TaxBreakdownUpdateSchema = z.object({
  taxType: z.string(),
  rate: z.number().nullable(),
  amount: z.number().nullable(),
  baseAmount: z.number().nullable(),
});

/**
 * AI decision for bill update reconciliation when linking a document to an existing bill.
 *
 * The agent analyzes the existing bill data and the new document extraction to
 * determine which fields should be updated with new information.
 */
export const BillUpdateReconciliationSchema = z.object({
  /** Whether any updates are recommended */
  shouldUpdate: z.boolean(),

  /** Brief summary of the reconciliation decision in PT-BR */
  summary: z.string(),

  /** Confidence level 0-100 */
  confidence: z.number(),

  /** Header field updates (billIssueDate, dueDate, competencyDate, vendorBillNumber, etc.) */
  headerUpdates: z.object({
    billIssueDate: z.string().nullable(),
    dueDate: z.string().nullable(),
    competencyDate: z.string().nullable(),
    vendorBillNumber: z.string().nullable(),
    nfeChaveAcesso: z.string().nullable(),
    description: z.string().nullable(),
  }),

  /** Financial updates */
  financialUpdates: z.object({
    totalAmount: z.number().nullable(),
    subtotalAmount: z.number().nullable(),
    discountAmount: z.number().nullable(),
    currency: z.string().nullable(),
  }),

  /** Tax breakdown to add/merge */
  taxBreakdown: z.array(TaxBreakdownUpdateSchema),

  /**
   * Whether to replace or merge line items. KEEP_EXISTING when the new document describes the
   * same charge(s) the bill already has (e.g. Invoice + Receipt of one subscription); MERGE only
   * appends charges not yet on the bill, and the system downgrades a MERGE whose lines would
   * exceed the reconciled bill total.
   */
  lineItemsAction: z
    .enum(['KEEP_EXISTING', 'REPLACE', 'MERGE'])
    .describe(
      'KEEP_EXISTING quando o novo documento descreve a(s) mesma(s) cobrança(s) já presentes no bill ' +
        '(ex.: invoice + recibo/comprovante/boleto/NFS-e do mesmo valor, período e fornecedor). ' +
        'MERGE apenas para cobranças que ainda não estão no bill, com o total do bill crescendo para incluí-las. ' +
        'REPLACE quando os itens do novo documento substituem os existentes.',
    ),

  /** Line items to add (when action is REPLACE or MERGE) */
  lineItems: z
    .array(LineItemUpdateSchema)
    .describe(
      'MERGE: somente os itens novos (nunca repita um item cujo valor já está no bill). ' +
        'REPLACE: todos os itens do novo documento. KEEP_EXISTING: lista vazia.',
    ),

  /** Withholding tax updates */
  withholdingUpdates: z.object({
    irrfAmount: z.number().nullable(),
    issAmount: z.number().nullable(),
    pisAmount: z.number().nullable(),
    cofinsAmount: z.number().nullable(),
    csllAmount: z.number().nullable(),
    inssAmount: z.number().nullable(),
    isRetained: z.boolean().nullable(),
  }),

  /** Detailed reasoning for the decision in PT-BR */
  reasoning: z.string(),
});

export type BillUpdateReconciliation = z.infer<typeof BillUpdateReconciliationSchema>;
export type LineItemUpdate = z.infer<typeof LineItemUpdateSchema>;
export type TaxBreakdownUpdate = z.infer<typeof TaxBreakdownUpdateSchema>;
